import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { audioManager } from '../game/audio/audioManager';
import { haptics } from '../game/haptics/haptics';
import { useAppStore } from '../state/store';
import { BackIcon, Button, IconButton, Segmented, Sheet, Toggle } from '../ui/components';

export function SettingsScreen({ onBack }: { onBack(): void }) {
  const { settings, updateSettings, resetProgress, storage } = useAppStore(
    useShallow((state) => ({
      settings: state.settings,
      updateSettings: state.updateSettings,
      resetProgress: state.resetProgress,
      storage: state.storage,
    })),
  );
  const [confirmReset, setConfirmReset] = useState(false);

  return (
    <div className="ar-screen">
      <header
        className="ar-row"
        data-justify="between"
        style={{ marginBottom: 'var(--ar-space-4)' }}
      >
        <IconButton label="Back" onClick={onBack}>
          <BackIcon />
        </IconButton>
        <h1 className="ar-subtitle">Settings</h1>
        <span style={{ width: '2.75rem' }} />
      </header>

      <div className="ar-stack" style={{ margin: '0 auto', overflowY: 'auto' }}>
        <section>
          <h2 className="ar-muted">Appearance</h2>

          <div className="ar-field">
            <span className="ar-field__label">Theme</span>
            <Segmented
              label="Theme"
              value={settings.theme}
              onChange={(theme) => void updateSettings({ theme })}
              options={[
                { value: 'system', label: 'Auto' },
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
              ]}
            />
          </div>

          <div className="ar-field">
            <span className="ar-field__label">High contrast</span>
            <Toggle
              label="High contrast"
              checked={settings.highContrast}
              onChange={(highContrast) => void updateSettings({ highContrast })}
            />
          </div>

          <div className="ar-field">
            <span className="ar-field__label">Motion</span>
            <Segmented
              label="Motion"
              value={settings.motion}
              onChange={(motion) => void updateSettings({ motion })}
              options={[
                { value: 'system', label: 'Auto' },
                { value: 'full', label: 'Full' },
                { value: 'reduced', label: 'Reduced' },
              ]}
            />
          </div>
        </section>

        <section>
          <h2 className="ar-muted">Sound &amp; feel</h2>

          <div className="ar-field">
            <span className="ar-field__label">Sound effects</span>
            <Toggle
              label="Sound effects"
              checked={settings.soundEnabled}
              onChange={(soundEnabled) => {
                audioManager.unlock();
                void updateSettings({ soundEnabled });
                if (soundEnabled) audioManager.play('ui-tap');
              }}
            />
          </div>

          <div className="ar-field">
            <span className="ar-field__label">Effects volume</span>
            <input
              className="ar-slider"
              type="range"
              min={0}
              max={100}
              value={Math.round(settings.soundVolume * 100)}
              aria-label="Effects volume"
              onChange={(event) =>
                void updateSettings({ soundVolume: Number(event.target.value) / 100 })
              }
            />
          </div>

          <div className="ar-field">
            <span className="ar-field__label">
              Vibration
              {!haptics.supported && <span className="ar-muted"> — not available here</span>}
            </span>
            <Toggle
              label="Vibration"
              checked={settings.hapticsEnabled}
              onChange={(hapticsEnabled) => void updateSettings({ hapticsEnabled })}
            />
          </div>
        </section>

        <section>
          <h2 className="ar-muted">Gameplay</h2>
          <div className="ar-field">
            <span className="ar-field__label">
              Show what blocked a tap
              <span className="ar-muted"> — flashes the arrow in the way</span>
            </span>
            <Toggle
              label="Show what blocked a tap"
              checked={settings.showBlockerHint}
              onChange={(showBlockerHint) => void updateSettings({ showBlockerHint })}
            />
          </div>
        </section>

        <section>
          <h2 className="ar-muted">Data</h2>
          <p className="ar-muted">
            Arrows stores progress on this device only. There is no account, no analytics and no
            network access after the first load.
            {!storage.storageAvailable && ' Storage is currently unavailable in this browser.'}
          </p>
          <Button
            variant="danger"
            size="block"
            style={{ marginTop: 'var(--ar-space-3)' }}
            onClick={() => setConfirmReset(true)}
          >
            Reset all progress
          </Button>
        </section>
      </div>

      {confirmReset && (
        <Sheet title="Reset all progress?" labelledBy="reset-title">
          <p className="ar-muted">
            Every cleared level and best time will be removed. This cannot be undone.
          </p>
          <div className="ar-stack" style={{ marginTop: 'var(--ar-space-4)' }}>
            <Button
              variant="danger"
              size="block"
              onClick={() => {
                void resetProgress();
                setConfirmReset(false);
              }}
            >
              Yes, reset everything
            </Button>
            <Button size="block" variant="ghost" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
